import { FEATURES } from '@/lib/Features'
import { logger } from '@/utils/logger'

const ACCESS_LEVELS = [
  'no_access',
  'admin',
  'executive',
  'manager',
  'individual_contributor',
  'hr',
  'finance',
  'member'
]

export const ACCESS_LEVELS_DICT = {
  NO_ACCESS: 'no_access',
  ADMIN: 'admin',
  EXECUTIVE: 'executive',
  MANAGER: 'manager',
  INDIVIDUAL_CONTRIBUTOR: 'individual_contributor',
  HR: 'hr',
  FINANCE: 'finance',
  REVIEWER: 'reviewer',
  MEMBER: 'member'
}

export const ACCESS_LEVELS_GROUPS = {
  ALL: [
    ACCESS_LEVELS_DICT.INDIVIDUAL_CONTRIBUTOR,
    ACCESS_LEVELS_DICT.MANAGER,
    ACCESS_LEVELS_DICT.ADMIN,
    ACCESS_LEVELS_DICT.EXECUTIVE,
    ACCESS_LEVELS_DICT.HR,
    ACCESS_LEVELS_DICT.FINANCE,
    ACCESS_LEVELS_DICT.MEMBER
  ],
  ABOVE_IC: [
    ACCESS_LEVELS_DICT.MANAGER,
    ACCESS_LEVELS_DICT.ADMIN,
    ACCESS_LEVELS_DICT.EXECUTIVE,
    ACCESS_LEVELS_DICT.HR,
    ACCESS_LEVELS_DICT.FINANCE,
    ACCESS_LEVELS_DICT.MEMBER
  ],
  TOP_LEVEL_FINANCE: [
    ACCESS_LEVELS_DICT.ADMIN,
    ACCESS_LEVELS_DICT.EXECUTIVE,
    ACCESS_LEVELS_DICT.FINANCE
  ],
  TOP_LEVEL_FINANCE_HR: [
    ACCESS_LEVELS_DICT.ADMIN,
    ACCESS_LEVELS_DICT.EXECUTIVE,
    ACCESS_LEVELS_DICT.FINANCE,
    ACCESS_LEVELS_DICT.HR
  ],
  TOP_LEVEL: [ACCESS_LEVELS_DICT.ADMIN, ACCESS_LEVELS_DICT.EXECUTIVE]
}

const FORMATTED_ACCESS_LEVEL_DICT = {
  [ACCESS_LEVELS_DICT.NO_ACCESS]: 'No Access',
  [ACCESS_LEVELS_DICT.ADMIN]: 'Admin',
  [ACCESS_LEVELS_DICT.EXECUTIVE]: 'Executive',
  [ACCESS_LEVELS_DICT.MANAGER]: 'Manager',
  [ACCESS_LEVELS_DICT.INDIVIDUAL_CONTRIBUTOR]: 'Individual Contributor',
  [ACCESS_LEVELS_DICT.HR]: 'HR',
  [ACCESS_LEVELS_DICT.FINANCE]: 'Finance',
  [ACCESS_LEVELS_DICT.MEMBER]: 'Member'
}

/**
 * Not all features are available for access control driven permissions
 */
export const ACCESS_CONTROL_FEATURES = [
  { id: FEATURES.OrgChart, name: 'Org Chart' },
  { id: FEATURES.Forecast, name: 'Main Org: Forecast' },
  { id: FEATURES.Directory, name: 'Main Org: Directory' },
  { id: FEATURES.Hub, name: 'Main Org: Workforce Hub' },
  { id: FEATURES.Scenarios, name: 'Scenarios' },
  { id: FEATURES.ExportData, name: 'Export Data' }
]

/**
 * Features that can be only enabled by Agentnoon admins
 */
export const ACCESS_CONTROL_FEATURES_BETA = [{ id: FEATURES.Productivity, name: 'Productivity' }]

const userHasRouteAccess = ({ role, permittedRoles, blockedRoles }) => {
  if (!permittedRoles && !blockedRoles) return true

  if (blockedRoles && blockedRoles.includes(role)) {
    logger.info("user's role is blocked from this page")
    return false
  }
  if (permittedRoles && !permittedRoles.includes(role)) {
    logger.info("user's role does not have access to this page")
    return false
  }

  return true
}

/**
 * Returns whether the selected people can bulk edit a field.
 * The field must be editable after backend permission consolidation, then each selected
 * person must map to a scope that grants edit permission for that field.
 *
 * @param {Object} fieldPermissions permissions returned by the field-permissions endpoint
 * @param {string} accessField field key used by access control permissions
 * @param {string[]} selectedPeople selected person ids
 * @returns {boolean} true when the field can be bulk edited
 */
const canBulkEditAccessField = ({ fieldPermissions, accessField, selectedPeople }) => {
  if (fieldPermissions?.consolidatedPermissions?.[accessField] !== 'edit') {
    return false
  }

  const permissions = fieldPermissions?.scopedPermissions?.[accessField] || []
  const scopeIdMapping = fieldPermissions?.scopeIdMapping || {}
  const personToScope = {}

  Object.entries(scopeIdMapping).forEach(([scopeId, personIds]) => {
    personIds?.forEach((personId) => {
      personToScope[personId] = scopeId
    })
  })

  return (
    selectedPeople.length > 0 &&
    selectedPeople.every((personId) => {
      const scopeId = personToScope[personId]

      return permissions.some(
        (permission) => permission.scopeId === scopeId && permission.permission === 'edit'
      )
    })
  )
}

export { ACCESS_LEVELS, FORMATTED_ACCESS_LEVEL_DICT, canBulkEditAccessField, userHasRouteAccess }
